package com.havn.app.features.properties.presentation

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnCold
import com.havn.app.core.designsystem.HavnDanger
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnBackground
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnPullToRefreshBox
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnMetric
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnStatCard
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextSecondary
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.designsystem.havnRefreshStartGate
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.core.platform.HavnMapCoordinate
import com.havn.app.core.platform.HavnPropertyMap
import com.havn.app.core.settings.HavnUnitPreferences
import com.havn.app.core.settings.LocalHavnUnitPreferences
import com.havn.app.core.settings.formatArea
import com.havn.app.core.settings.formatCurrency
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.domain.AgencyRole
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseStatus
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.domain.PropertyPhoto
import com.havn.app.features.properties.domain.PropertyStatus
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlin.math.roundToInt

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PropertiesScreen(
    viewModel: PropertiesViewModel,
    photoPicker: HavnPhotoPicker,
    propertyOpenings: List<OpenHouse> = emptyList(),
    onOpenPropertyDetail: (String) -> Unit = {},
    onScheduleOpenHouse: (String) -> Unit = {},
    onOpenOpening: (String) -> Unit = {},
    onOpenOwnerContact: (String) -> Unit = {},
    onGenerateSellerReport: (String) -> Unit = {},
    canGenerateSellerReport: Boolean = false,
    generatingSellerReportPropertyId: String? = null
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    val unitPreferences = LocalHavnUnitPreferences.current

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = PropertiesMode::navigationDepth,
        modifier = Modifier.fillMaxSize()
    ) { mode ->
        if (mode == PropertiesMode.List) {
            PropertiesListHome(
                state = state,
                onAdd = viewModel::startAdd,
                onOpen = { propertyId ->
                    viewModel.openProperty(propertyId)
                    onOpenPropertyDetail(propertyId)
                },
                onSearchChanged = viewModel::updateSearchQuery,
                onLoadMore = viewModel::loadMoreProperties,
                onRefresh = viewModel::refresh,
                onDismissError = viewModel::clearError
            )
            return@HavnPushNavigation
        }

        HavnScreenFrame(
            eyebrow = if (mode == PropertiesMode.Detail || mode == PropertiesMode.Add || mode == PropertiesMode.Edit || mode == PropertiesMode.Locate) "" else strings.propertiesEyebrow,
            title = when (mode) {
                PropertiesMode.List -> strings.navProperties
                PropertiesMode.Detail -> state.selectedProperty?.title ?: strings.propertyFallback
                PropertiesMode.Add -> strings.addProperty
                PropertiesMode.Edit -> strings.editProperty
                PropertiesMode.Locate -> strings.locateProperty
            },
            subtitle = if (mode == PropertiesMode.Detail || mode == PropertiesMode.Edit || mode == PropertiesMode.Locate) "" else strings.propertiesSubtitle,
            metrics = if (mode == PropertiesMode.Detail || mode == PropertiesMode.Add || mode == PropertiesMode.Edit || mode == PropertiesMode.Locate) {
                emptyList()
            } else {
                listOf(
                    HavnMetric(strings.commonActive, state.activeCount.toString(), strings.propertiesLiveMetric),
                    HavnMetric(strings.commonPlanned, state.plannedCount.toString(), strings.propertiesPipelineMetric),
                    HavnMetric(strings.commonVisitors, state.totalVisitors.toString(), strings.propertiesAllTimeMetric)
                )
            },
            backLabel = when (mode) {
                PropertiesMode.List -> null
                PropertiesMode.Detail,
                PropertiesMode.Add -> strings.navProperties
                PropertiesMode.Edit,
                PropertiesMode.Locate -> state.selectedProperty?.title ?: strings.propertyFallback
            },
            onBack = when (mode) {
                PropertiesMode.List -> null
                PropertiesMode.Detail -> viewModel::closeDetail
                PropertiesMode.Add,
                PropertiesMode.Edit -> viewModel::cancelForm
                PropertiesMode.Locate -> viewModel::cancelLocateProperty
            },
            isRefreshing = state.isLoading,
            onRefresh = viewModel::refresh
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                state.error?.let { error ->
                    ErrorPanel(error = error, onDismiss = viewModel::clearError)
                }

                when (mode) {
                    PropertiesMode.List -> Unit
                    PropertiesMode.Detail -> state.selectedProperty?.let { property ->
                        PropertyDetail(
                            property = property,
                            isSaving = state.isSaving,
                            isDetailLoading = state.isDetailLoading,
                            isUploading = state.isUploadingPhoto,
                            strings = strings,
                            unitPreferences = unitPreferences,
                            currentAgentId = state.currentAgentId,
                            agencyMembers = state.agencyMembers,
                            assignedAgentIds = state.selectedPropertyAssignmentIds,
                            canShowTeamAccess = state.isProPlan,
                            isAssigningProperty = state.isAssigningProperty,
                            ownerContacts = state.ownerContacts,
                            openings = propertyOpenings.filter { it.propertyId == property.id },
                            onAssignPropertyAccess = viewModel::assignSelectedPropertyToTeam,
                            onOwnerContactSelected = viewModel::updateSelectedPropertyOwner,
                            onOpenOwnerContact = onOpenOwnerContact,
                            onOpenOpening = onOpenOpening,
                            onGenerateSellerReport = onGenerateSellerReport,
                            canGenerateSellerReport = canGenerateSellerReport,
                            isGeneratingSellerReport = generatingSellerReportPropertyId == property.id,
                            onEdit = { viewModel.startEdit(property.id) },
                            onScheduleOpenHouse = { onScheduleOpenHouse(property.id) },
                            onDelete = viewModel::requestDeleteSelected
                        )
                    } ?: EmptyState(
                        title = strings.propertyUnavailableTitle,
                        body = strings.propertyUnavailableBody,
                        actionText = strings.backToProperties,
                        onAction = viewModel::closeDetail
                    )

                    PropertiesMode.Add,
                    PropertiesMode.Edit -> PropertyForm(
                        mode = mode,
                        form = state.form,
                        ownerContacts = state.ownerContacts,
                        agencyMembers = state.agencyMembers,
                        canShowTeamAccess = state.isProPlan,
                        isSaving = state.isSaving,
                        isUploadingPhoto = state.isUploadingPhoto,
                        canUploadPhoto = mode == PropertiesMode.Edit,
                        property = state.selectedProperty,
                        strings = strings,
                        unitPreferences = unitPreferences,
                        onChange = viewModel::updateForm,
                        onSave = viewModel::saveProperty,
                        onLocateProperty = viewModel::startLocateProperty,
                        onUploadPhoto = {
                            photoPicker.pick { pickedPhoto ->
                                viewModel.uploadHeroPhoto(pickedPhoto)
                            }
                        },
                        onDeletePhoto = viewModel::deletePhoto,
                        onMovePhoto = viewModel::movePhoto,
                        onMakeMainPhoto = viewModel::makePhotoMain
                    )

                    PropertiesMode.Locate -> PropertyLocationScreen(
                        form = state.form,
                        onCoordinateChanged = { coordinate ->
                            viewModel.updatePendingLocation(coordinate.latitude, coordinate.longitude)
                        }
                    )
                }
            }
        }
    }

    if (state.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = viewModel::dismissDeleteConfirmation,
            title = { Text(strings.deletePropertyTitle) },
            text = {
                Text(strings.deletePropertyBody)
            },
            confirmButton = {
                TextButton(onClick = viewModel::confirmDeleteSelected) {
                    Text(strings.commonDelete, color = HavnDanger)
                }
            },
            dismissButton = {
                TextButton(onClick = viewModel::dismissDeleteConfirmation) {
                    Text(strings.commonCancel)
                }
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun PropertiesListHome(
    state: PropertiesUiState,
    onAdd: () -> Unit,
    onOpen: (String) -> Unit,
    onSearchChanged: (String) -> Unit,
    onLoadMore: () -> Unit,
    onRefresh: () -> Unit,
    onDismissError: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    var selectedFilter by remember { mutableStateOf(PropertyListFilter.All) }
    val filteredProperties = state.properties.filter { property ->
        val matchesStatus = when (selectedFilter) {
            PropertyListFilter.All -> true
            PropertyListFilter.Active -> property.status == PropertyStatus.Active
            PropertyListFilter.Planned -> property.status == PropertyStatus.Planned
            PropertyListFilter.Archived -> property.status == PropertyStatus.Archived
        }
        matchesStatus
    }
    val listState = rememberLazyListState()
    var gestureStartedAtTop by remember { mutableStateOf(false) }

    HavnPullToRefreshBox(
        isRefreshing = state.isLoading,
        onRefresh = {
            if (gestureStartedAtTop) {
                onRefresh()
            }
        },
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(listState) { gestureStartedAtTop = it }
    ) {
        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
                .padding(horizontal = 18.dp, vertical = 18.dp),
            verticalArrangement = Arrangement.spacedBy(HavnListDefaults.headerToSearchGap)
        ) {
            item(key = "properties-header") {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = strings.navProperties,
                        modifier = Modifier.weight(1f),
                        color = theme.primaryText,
                        style = HavnTopHeaderTextStyle
                    )
                    PropertiesIconCircleButton(
                        icon = HavnIconKey.Plus,
                        contentDescription = strings.addProperty,
                        onClick = onAdd
                    )
                }
            }

            state.error?.let { error ->
                item(key = "properties-error") {
                    ErrorPanel(error = error, onDismiss = onDismissError)
                }
            }

            item(key = "properties-filters") {
                Column(verticalArrangement = Arrangement.spacedBy(HavnListDefaults.searchToFilterGap)) {
                    HavnSearchField(
                        value = state.searchQuery,
                        onValueChange = onSearchChanged,
                        placeholder = strings.searchProperties
                    )

                    PropertyStatusSegments(
                        selected = selectedFilter,
                        onSelected = { selectedFilter = it }
                    )
                }
            }

            if (state.isLoading) {
                items(6, key = { "property-skeleton-$it" }) {
                    PropertyCardSkeleton()
                }
            } else if (state.properties.isEmpty()) {
                item(key = "properties-empty") {
                    EmptyState(
                        title = strings.noPropertiesTitle,
                        body = strings.noPropertiesBody,
                        actionText = strings.addProperty,
                        onAction = onAdd
                    )
                }
            } else if (filteredProperties.isEmpty()) {
                item(key = "properties-empty-filtered") {
                    EmptyState(
                        title = strings.noMatchingPropertiesTitle,
                        body = strings.noMatchingPropertiesBody
                    )
                }
            } else {
                items(filteredProperties, key = { it.id }) { property ->
                    CompactPropertyRow(
                        property = property,
                        onClick = { onOpen(property.id) }
                    )
                }
                if (state.hasMoreProperties) {
                    item(key = "properties-load-more") {
                        HavnButton(
                            text = "Load more",
                            onClick = onLoadMore,
                            modifier = Modifier.fillMaxWidth(),
                            style = HavnButtonStyle.Secondary,
                            loading = state.isLoadingMore
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PropertiesIconCircleButton(
    icon: HavnIconKey,
    contentDescription: String,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .size(42.dp)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            HavnIcon(icon, contentDescription = contentDescription, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
        }
    }
}

private enum class PropertyListFilter {
    All,
    Active,
    Planned,
    Archived
}

@Composable
private fun PropertyStatusSegments(
    selected: PropertyListFilter,
    onSelected: (PropertyListFilter) -> Unit
) {
    val strings = LocalHavnStrings.current
    val segments = listOf(
        PropertyListFilter.All to strings.commonAll,
        PropertyListFilter.Active to strings.commonActive,
        PropertyListFilter.Planned to strings.commonPlanned,
        PropertyListFilter.Archived to strings.commonArchived
    )

    Row(
        horizontalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap),
        modifier = Modifier.fillMaxWidth()
    ) {
        segments.forEach { (filter, label) ->
            HavnListFilterChip(
                label = label,
                selected = selected == filter,
                onClick = { onSelected(filter) },
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun CompactPropertyRow(property: Property, onClick: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        color = theme.card,
        shape = MaterialTheme.shapes.large,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(10.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Box(
                modifier = Modifier
                    .width(116.dp)
                    .height(112.dp)
                    .clip(MaterialTheme.shapes.medium)
            ) {
                PropertyImageHeader(property)
                HavnStatusBadge(
                    status = property.status.toHavnStatus(),
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(7.dp)
                )
            }

            Box(
                modifier = Modifier
                    .weight(1f)
                    .height(112.dp)
            ) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.SpaceBetween
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
                        verticalAlignment = Alignment.Top
                    ) {
                        Column(
                            modifier = Modifier.weight(1f),
                            verticalArrangement = Arrangement.spacedBy(3.dp)
                        ) {
                            Text(
                                property.title,
                                color = theme.primaryText,
                                style = MaterialTheme.typography.titleMedium,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                property.displayCity.ifBlank { property.displayAddress },
                                color = theme.secondaryText,
                                style = MaterialTheme.typography.bodyMedium,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        PropertyMetricItem(
                            icon = HavnIconKey.Calendar,
                            value = property.stats.openingCount.toString(),
                            label = strings.openingsTitle,
                            modifier = Modifier.weight(1f)
                        )
                        PropertyMetricItem(
                            icon = HavnIconKey.Leads,
                            value = property.stats.totalVisitors.toString(),
                            label = strings.leadsMetric.replaceFirstChar { it.uppercase() },
                            modifier = Modifier.weight(1f)
                        )
                        PropertyMetricItem(
                            icon = HavnIconKey.Mail,
                            value = property.stats.followUpsSent.toString(),
                            label = strings.commonFollowUps,
                            modifier = Modifier.weight(1.2f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PropertyCardSkeleton() {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = theme.card,
        shape = MaterialTheme.shapes.large,
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.55f))
    ) {
        Row(
            modifier = Modifier.padding(10.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            HavnSkeletonBlock(
                modifier = Modifier
                    .width(116.dp)
                    .height(112.dp)
                    .clip(MaterialTheme.shapes.medium)
            )
            Column(
                modifier = Modifier
                    .weight(1f)
                    .height(112.dp),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    HavnSkeletonBlock(Modifier.fillMaxWidth(0.72f).height(18.dp))
                    HavnSkeletonBlock(Modifier.fillMaxWidth(0.52f).height(14.dp))
                }
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    repeat(3) {
                        Column(
                            modifier = Modifier.weight(1f),
                            verticalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            HavnSkeletonBlock(Modifier.fillMaxWidth(0.45f).height(14.dp))
                            HavnSkeletonBlock(Modifier.fillMaxWidth(0.72f).height(12.dp))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HavnSkeletonBlock(modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = modifier
            .clip(MaterialTheme.shapes.small)
            .background(theme.surfaceElevated.copy(alpha = 0.82f))
    )
}

@Composable
private fun PropertyMetricItem(
    icon: HavnIconKey,
    value: String,
    label: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(3.dp)
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(
                icon = icon,
                contentDescription = null,
                modifier = Modifier.size(14.dp),
                tint = theme.secondaryText
            )
            Text(
                value,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        Text(
            label,
            color = theme.mutedText,
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun PropertyDetail(
    property: Property,
    isSaving: Boolean,
    isDetailLoading: Boolean,
    isUploading: Boolean,
    strings: com.havn.app.core.localization.HavnStrings,
    unitPreferences: HavnUnitPreferences,
    currentAgentId: String?,
    agencyMembers: List<AgencyMember>,
    assignedAgentIds: List<String>,
    canShowTeamAccess: Boolean,
    isAssigningProperty: Boolean,
    ownerContacts: List<Client>,
    openings: List<OpenHouse>,
    onAssignPropertyAccess: (List<String>) -> Unit,
    onOwnerContactSelected: (String?) -> Unit,
    onOpenOwnerContact: (String) -> Unit,
    onOpenOpening: (String) -> Unit,
    onGenerateSellerReport: (String) -> Unit,
    canGenerateSellerReport: Boolean,
    isGeneratingSellerReport: Boolean,
    onEdit: () -> Unit,
    onScheduleOpenHouse: () -> Unit,
    onDelete: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val defaultPhoto = property.photos.firstOrNull { it.storagePath == property.heroPhotoPath }
        ?: property.photos.firstOrNull { it.isHero }
        ?: property.photos.firstOrNull()
    var selectedPhotoId by remember(property.id) { mutableStateOf(defaultPhoto?.id) }
    val selectedPhoto = property.photos.firstOrNull { it.id == selectedPhotoId } ?: defaultPhoto
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val wide = havnIsWebShell && maxWidth >= 960.dp
        if (wide) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.lg),
                verticalAlignment = Alignment.Top
            ) {
                PropertyHeroInfoCard(
                    property = property,
                    selectedPhoto = selectedPhoto,
                    selectedPhotoId = selectedPhoto?.id,
                    strings = strings,
                    unitPreferences = unitPreferences,
                    onPhotoSelected = { selectedPhotoId = it },
                    modifier = Modifier.weight(1.55f)
                )
                PropertySidePanel(
                    property = property,
                    currentAgentId = currentAgentId,
                    agencyMembers = agencyMembers,
                    assignedAgentIds = assignedAgentIds,
                    canShowTeamAccess = canShowTeamAccess,
                    isAssigningProperty = isAssigningProperty,
                    ownerContacts = ownerContacts,
                    openings = openings,
                    onAssignPropertyAccess = onAssignPropertyAccess,
                    onOwnerContactSelected = onOwnerContactSelected,
                    onOpenOwnerContact = onOpenOwnerContact,
                    onOpenOpening = onOpenOpening,
                    onGenerateSellerReport = onGenerateSellerReport,
                    canGenerateSellerReport = canGenerateSellerReport,
                    isGeneratingSellerReport = isGeneratingSellerReport,
                    strings = strings,
                    onScheduleOpenHouse = onScheduleOpenHouse,
                    onEdit = onEdit,
                    onDelete = onDelete,
                    isSaving = isSaving,
                    modifier = Modifier.widthIn(max = 420.dp).weight(1f)
                )
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                if (isDetailLoading) {
                    LoadingState("Loading property details")
                }
                PropertyHeroInfoCard(
                    property = property,
                    selectedPhoto = selectedPhoto,
                    selectedPhotoId = selectedPhoto?.id,
                    strings = strings,
                    unitPreferences = unitPreferences,
                    onPhotoSelected = { selectedPhotoId = it },
                    modifier = Modifier.fillMaxWidth()
                )
                PropertySidePanel(
                    property = property,
                    currentAgentId = currentAgentId,
                    agencyMembers = agencyMembers,
                    assignedAgentIds = assignedAgentIds,
                    canShowTeamAccess = canShowTeamAccess,
                    isAssigningProperty = isAssigningProperty,
                    ownerContacts = ownerContacts,
                    openings = openings,
                    onAssignPropertyAccess = onAssignPropertyAccess,
                    onOwnerContactSelected = onOwnerContactSelected,
                    onOpenOwnerContact = onOpenOwnerContact,
                    onOpenOpening = onOpenOpening,
                    onGenerateSellerReport = onGenerateSellerReport,
                    canGenerateSellerReport = canGenerateSellerReport,
                    isGeneratingSellerReport = isGeneratingSellerReport,
                    strings = strings,
                    onScheduleOpenHouse = onScheduleOpenHouse,
                    onEdit = onEdit,
                    onDelete = onDelete,
                    isSaving = isSaving,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun PropertyHeroInfoCard(
    property: Property,
    selectedPhoto: PropertyPhoto?,
    selectedPhotoId: String?,
    strings: com.havn.app.core.localization.HavnStrings,
    unitPreferences: HavnUnitPreferences,
    onPhotoSelected: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(
        modifier = modifier,
        imageHeader = {
            Box(modifier = Modifier.fillMaxSize()) {
                PropertyImageHeader(property = property, selectedPhoto = selectedPhoto)
                HavnStatusBadge(
                    status = property.status.toHavnStatus(),
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .padding(HavnSpacing.md)
                )
            }
        }
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            PropertyPhotoThumbnailStrip(
                property = property,
                selectedPhotoId = selectedPhotoId,
                onPhotoSelected = onPhotoSelected
            )
            Text(property.title, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
            Text(property.displayAddress, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            Text(property.displayCity, color = theme.mutedText, style = MaterialTheme.typography.bodyMedium)
            property.mapCoordinate()?.let { coordinate ->
                HavnButton(
                    text = strings.navigate,
                    onClick = { HavnExternalActions.openNavigation(coordinate.latitude, coordinate.longitude) },
                    style = HavnButtonStyle.Secondary,
                    modifier = if (havnIsWebShell) Modifier.widthIn(max = 240.dp) else Modifier.fillMaxWidth(),
                    leadingIcon = HavnIconKey.ArrowRight
                )
            }
            Text(property.askingPrice.formatCurrency(unitPreferences), color = theme.primaryAccent, style = MaterialTheme.typography.headlineMedium)
            property.interiorSqft?.let { area ->
                Text(area.formatArea(unitPreferences), color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            }
            property.description?.takeIf { it.isNotBlank() }?.let {
                Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            }
        }
    }
}

@Composable
private fun PropertySidePanel(
    property: Property,
    currentAgentId: String?,
    agencyMembers: List<AgencyMember>,
    assignedAgentIds: List<String>,
    canShowTeamAccess: Boolean,
    isAssigningProperty: Boolean,
    ownerContacts: List<Client>,
    openings: List<OpenHouse>,
    onAssignPropertyAccess: (List<String>) -> Unit,
    onOwnerContactSelected: (String?) -> Unit,
    onOpenOwnerContact: (String) -> Unit,
    onOpenOpening: (String) -> Unit,
    onGenerateSellerReport: (String) -> Unit,
    canGenerateSellerReport: Boolean,
    isGeneratingSellerReport: Boolean,
    strings: com.havn.app.core.localization.HavnStrings,
    onScheduleOpenHouse: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    isSaving: Boolean,
    modifier: Modifier = Modifier
) {
    val currentMemberRole = agencyMembers.firstOrNull { it.agentId == currentAgentId }?.role
    val canAssignProperty = property.agencyId != null &&
        currentMemberRole != AgencyRole.Viewer &&
        (
            currentMemberRole == AgencyRole.Owner ||
                currentMemberRole == AgencyRole.Admin ||
                property.ownerAgentId == currentAgentId ||
                property.agentId == currentAgentId
        )
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
        ownerContacts.firstOrNull { it.id == property.ownerContactId }?.let { ownerContact ->
            PropertyOwnerContactDetailCard(
                contact = ownerContact,
                strings = strings,
                onOpen = { onOpenOwnerContact(ownerContact.id) }
            )
        }
        PropertyOpeningsCard(
            openings = openings,
            strings = strings,
            onOpenOpening = onOpenOpening
        )
        HavnButton(
            text = strings.openHouseButton,
            onClick = onScheduleOpenHouse,
            modifier = Modifier.fillMaxWidth(),
            leadingIcon = HavnIconKey.Calendar
        )
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            HavnStatCard(property.stats.totalVisitors.toString(), strings.commonVisitors, modifier = Modifier.weight(1f), icon = HavnIconKey.Users)
            HavnStatCard(property.stats.hotLeads.toString(), strings.commonHot, modifier = Modifier.weight(1f), icon = HavnIconKey.TrendUp)
        }
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            HavnStatCard(property.stats.followUpsSent.toString(), strings.commonFollowUps, modifier = Modifier.weight(1f), icon = HavnIconKey.Mail)
            HavnStatCard("${property.stats.conversionRate.roundToInt()}%", strings.conversionMetric, modifier = Modifier.weight(1f), icon = HavnIconKey.Chart)
        }
        if (canGenerateSellerReport) {
            HavnButton(
                text = strings.generateSellerReport,
                onClick = { onGenerateSellerReport(property.id) },
                loading = isGeneratingSellerReport,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Reports
            )
        }
        if (canShowTeamAccess && canAssignProperty && agencyMembers.isNotEmpty()) {
            PropertyTeamAccessCard(
                members = agencyMembers,
                assignedAgentIds = assignedAgentIds,
                strings = strings,
                isSaving = isAssigningProperty,
                onSave = onAssignPropertyAccess
            )
        }
        HavnButton(
            text = strings.editProperty,
            onClick = onEdit,
            modifier = Modifier.fillMaxWidth(),
            style = HavnButtonStyle.Secondary
        )
        HavnButton(
            text = strings.deleteProperty,
            onClick = onDelete,
            modifier = Modifier.fillMaxWidth(),
            style = HavnButtonStyle.Danger,
            loading = isSaving
        )
    }
}

@Composable
private fun PropertyOwnerContactDetailCard(
    contact: Client,
    strings: com.havn.app.core.localization.HavnStrings,
    onOpen: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(
                strings.propertyOwnerContactTitle,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleLarge
            )
            OwnerContactRow(
                contact = contact,
                selected = false,
                onClick = onOpen,
                trailingIcon = HavnIconKey.ArrowRight
            )
        }
    }
}

@Composable
private fun PropertyTeamAccessCard(
    members: List<AgencyMember>,
    assignedAgentIds: List<String>,
    strings: com.havn.app.core.localization.HavnStrings,
    isSaving: Boolean,
    onSave: (List<String>) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    var showDialog by remember { mutableStateOf(false) }
    val assignedNames = members
        .filter { member -> member.agentId in assignedAgentIds }
        .take(3)
        .joinToString { it.fullName }
        .ifBlank { strings.onlyListingOwner }
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                HavnIcon(HavnIconKey.Users, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
                Text(strings.teamAccessTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            }
            Text(
                assignedNames,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )
            HavnButton(
                text = strings.assignTeam,
                onClick = { showDialog = true },
                style = HavnButtonStyle.Secondary,
                loading = isSaving,
                modifier = if (havnIsWebShell) Modifier.widthIn(max = 260.dp) else Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Sliders
            )
        }
    }

    if (showDialog) {
        PropertyTeamAccessDialog(
            members = members,
            assignedAgentIds = assignedAgentIds,
            strings = strings,
            isSaving = isSaving,
            onDismiss = { showDialog = false },
            onSave = { selectedIds ->
                showDialog = false
                onSave(selectedIds)
            }
        )
    }
}

@Composable
private fun PropertyOpeningsCard(
    openings: List<OpenHouse>,
    strings: com.havn.app.core.localization.HavnStrings,
    onOpenOpening: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
                verticalAlignment = Alignment.CenterVertically
            ) {
                HavnIcon(HavnIconKey.CalendarClock, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
                Text(strings.propertyOpeningsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            }
            if (openings.isEmpty()) {
                Text(strings.propertyNoOpenings, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            } else {
                openings.sortedByDescending { it.startsAt }.forEach { opening ->
                    PropertyOpeningRow(opening = opening, onClick = { onOpenOpening(opening.id) })
                }
            }
        }
    }
}

@Composable
private fun PropertyOpeningRow(
    opening: OpenHouse,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = theme.surface.copy(alpha = 0.62f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.68f))
    ) {
        Row(
            modifier = Modifier.padding(HavnSpacing.sm),
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(HavnIconKey.Calendar, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(opening.displayTitle, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text("${opening.startsAt.displayDateTime()} - ${opening.endsAt.displayDateTime()}", color = theme.secondaryText, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            HavnStatusBadge(status = opening.status.toHavnStatus())
            HavnIcon(HavnIconKey.ArrowRight, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(16.dp))
        }
    }
}

private fun OpenHouseStatus.toHavnStatus(): HavnStatus = when (this) {
    OpenHouseStatus.Live -> HavnStatus.Active
    OpenHouseStatus.Completed,
    OpenHouseStatus.Cancelled -> HavnStatus.Archived
    OpenHouseStatus.Scheduled -> HavnStatus.Planned
}

@Composable
private fun PropertyTeamAccessDialog(
    members: List<AgencyMember>,
    assignedAgentIds: List<String>,
    strings: com.havn.app.core.localization.HavnStrings,
    isSaving: Boolean,
    onDismiss: () -> Unit,
    onSave: (List<String>) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    var selectedIds by remember(assignedAgentIds, members) { mutableStateOf(assignedAgentIds.toSet()) }
    val activeMemberIds = members.mapNotNull { it.agentId }.toSet()
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = theme.card,
        title = { Text(strings.assignPropertyTitle, color = theme.primaryText) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                Text(
                    strings.assignPropertyBody,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                PropertyAssigneeRow(
                    title = strings.wholeTeam,
                    subtitle = "${members.size} ${strings.activeMembers}",
                    selected = selectedIds.containsAll(activeMemberIds),
                    onClick = { selectedIds = activeMemberIds }
                )
                LazyColumn(
                    modifier = Modifier.heightIn(max = 340.dp),
                    verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
                ) {
                    items(members, key = { it.agentId ?: it.email }) { member ->
                        val memberAgentId = member.agentId ?: return@items
                        PropertyAssigneeRow(
                            title = member.fullName,
                            subtitle = "${member.role.displayName} · ${member.email}",
                            selected = memberAgentId in selectedIds,
                            onClick = {
                                selectedIds = if (memberAgentId in selectedIds) {
                                    selectedIds - memberAgentId
                                } else {
                                    selectedIds + memberAgentId
                                }
                            }
                        )
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = { onSave(selectedIds.toList()) },
                enabled = !isSaving
            ) {
                Text(strings.commonSave, color = theme.primaryAccent)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.commonCancel, color = theme.secondaryText)
            }
        }
    )
}

@Composable
private fun PropertyAssigneeRow(
    title: String,
    subtitle: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.medium)
            .clickable(onClick = onClick),
        color = if (selected) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border),
        shape = MaterialTheme.shapes.medium
    ) {
        Row(
            modifier = Modifier.padding(HavnSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
        ) {
            Surface(
                modifier = Modifier.size(34.dp),
                shape = CircleShape,
                color = if (selected) theme.primaryAccent else theme.surfaceElevated,
                border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    HavnIcon(
                        icon = if (selected) HavnIconKey.Check else HavnIconKey.Users,
                        contentDescription = null,
                        tint = if (selected) theme.background else theme.secondaryText,
                        modifier = Modifier.size(16.dp)
                    )
                }
            }
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(title, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
                Text(subtitle, color = theme.secondaryText, style = MaterialTheme.typography.labelMedium)
            }
        }
    }
}

@Composable
private fun PropertyPhotoThumbnailStrip(property: Property) {
    PropertyPhotoThumbnailStrip(
        property = property,
        selectedPhotoId = property.photos.firstOrNull { it.storagePath == property.heroPhotoPath }?.id
            ?: property.photos.firstOrNull { it.isHero }?.id
            ?: property.photos.firstOrNull()?.id,
        onPhotoSelected = {}
    )
}

@Composable
private fun PropertyPhotoThumbnailStrip(
    property: Property,
    selectedPhotoId: String?,
    onPhotoSelected: (String) -> Unit
) {
    val photos = property.photos
    val theme = LocalHavnThemeTokens.current
    if (photos.size <= 1) return

    LazyRow(
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
        modifier = Modifier.fillMaxWidth()
    ) {
        items(photos, key = { it.id }) { photo ->
            val isMain = photo.id == selectedPhotoId
            Box(
                modifier = Modifier
                    .size(58.dp)
                    .clip(MaterialTheme.shapes.medium)
                    .clickable { onPhotoSelected(photo.id) }
                    .border(
                        width = if (isMain) 2.dp else 1.dp,
                        color = if (isMain) theme.primaryAccent else theme.border,
                        shape = MaterialTheme.shapes.medium
                    )
                    .background(theme.surface)
            ) {
                val rendered = HavnDatabaseImage(
                    dataUrl = photo.thumbnailDataUrl,
                    contentDescription = photo.altText ?: property.title,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnIcon(
                        icon = HavnIconKey.Properties,
                        contentDescription = null,
                        tint = theme.primaryAccent,
                        modifier = Modifier
                            .align(Alignment.Center)
                            .size(22.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun PropertyForm(
    mode: PropertiesMode,
    form: PropertyFormState,
    ownerContacts: List<Client>,
    agencyMembers: List<AgencyMember>,
    canShowTeamAccess: Boolean,
    isSaving: Boolean,
    isUploadingPhoto: Boolean,
    canUploadPhoto: Boolean,
    property: Property?,
    strings: com.havn.app.core.localization.HavnStrings,
    unitPreferences: HavnUnitPreferences,
    onChange: (PropertyFormState.() -> PropertyFormState) -> Unit,
    onSave: () -> Unit,
    onLocateProperty: () -> Unit,
    onUploadPhoto: () -> Unit,
    onDeletePhoto: (String) -> Unit,
    onMovePhoto: (String, Int) -> Unit,
    onMakeMainPhoto: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val actionButtonModifier = if (havnIsWebShell) {
        Modifier.widthIn(max = 360.dp)
    } else {
        Modifier.fillMaxWidth()
    }
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            form.validationError?.let {
                Text(it.localizedPropertyValidation(strings), color = HavnDanger, style = MaterialTheme.typography.bodyMedium)
            }
            HavnTextField(form.title, { value -> onChange { copy(title = value, validationError = null) } }, strings.propertyTitleLabel)
            HavnTextField(form.addressLine1, { value -> onChange { copy(addressLine1 = value, validationError = null) } }, strings.propertyAddressLine1Label)
            HavnTextField(form.addressLine2, { value -> onChange { copy(addressLine2 = value) } }, strings.propertyAddressLine2Label)
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                HavnTextField(
                    value = form.city,
                    onValueChange = { value -> onChange { copy(city = value, validationError = null) } },
                    label = strings.propertyCityLabel,
                    modifier = Modifier.weight(1f)
                )
                HavnTextField(
                    value = form.region,
                    onValueChange = { value -> onChange { copy(region = value, validationError = null) } },
                    label = strings.propertyRegionLabel,
                    modifier = Modifier.weight(1f)
                )
            }
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                HavnTextField(
                    value = form.postalCode,
                    onValueChange = { value -> onChange { copy(postalCode = value, validationError = null) } },
                    label = strings.propertyPostalCodeLabel,
                    modifier = Modifier.weight(1f),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                )
                HavnTextField(
                    value = form.country,
                    onValueChange = { value -> onChange { copy(country = value) } },
                    label = strings.propertyCountryLabel,
                    modifier = Modifier.weight(1f)
                )
            }
            if (mode == PropertiesMode.Edit) {
                HavnButton(
                    text = strings.locateProperty,
                    onClick = onLocateProperty,
                    style = HavnButtonStyle.Secondary,
                    modifier = actionButtonModifier,
                    leadingIcon = HavnIconKey.Home
                )
                form.locationLabel()?.let { location ->
                    Text(location, color = theme.mutedText, style = MaterialTheme.typography.bodySmall)
                }
            }
            HavnTextField(
                value = form.askingPrice,
                onValueChange = { value -> onChange { copy(askingPrice = value.filter { it.isDigit() }, validationError = null) } },
                label = "${strings.propertyAskingPriceLabel} (${unitPreferences.currency.code})",
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                leadingIcon = HavnIconKey.Chart
            )
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                HavnTextField(
                    value = form.bedrooms,
                    onValueChange = { value -> onChange { copy(bedrooms = value.numberText()) } },
                    label = strings.propertyBedsLabel,
                    modifier = Modifier.weight(1f),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal)
                )
                HavnTextField(
                    value = form.bathrooms,
                    onValueChange = { value -> onChange { copy(bathrooms = value.numberText()) } },
                    label = strings.propertyBathsLabel,
                    modifier = Modifier.weight(1f),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal)
                )
                HavnTextField(
                    value = form.interiorSqft,
                    onValueChange = { value -> onChange { copy(interiorSqft = value.filter { it.isDigit() }) } },
                    label = "${strings.propertyInteriorLabel} ${unitPreferences.areaLabel}",
                    modifier = Modifier.weight(1f),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                )
            }
            StatusPicker(form.status, strings) { status -> onChange { copy(status = status) } }
            HavnTextField(
                value = form.description,
                onValueChange = { value -> onChange { copy(description = value) } },
                label = strings.propertyDescriptionLabel,
                singleLine = false
            )
        }
    }

    PropertyOwnerContactCard(
        contacts = ownerContacts,
        selectedContactId = form.ownerContactId,
        strings = strings,
        onSelect = { contactId -> onChange { copy(ownerContactId = contactId) } }
    )

    if (canShowTeamAccess && mode == PropertiesMode.Add && agencyMembers.isNotEmpty()) {
        PropertyTeamAccessCard(
            members = agencyMembers,
            assignedAgentIds = form.assignedAgentIds,
            strings = strings,
            isSaving = false,
            onSave = { agentIds -> onChange { copy(assignedAgentIds = agentIds.distinct()) } }
        )
    }

    if (canUploadPhoto) {
        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                Text(strings.photosTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                Text(
                    strings.photosBody,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                HavnButton(
                    text = strings.uploadPhoto,
                    onClick = onUploadPhoto,
                    style = HavnButtonStyle.Secondary,
                    loading = isUploadingPhoto,
                    enabled = !isUploadingPhoto,
                    modifier = actionButtonModifier,
                    leadingIcon = HavnIconKey.Plus
                )
                val photos = property?.photos.orEmpty()
                if (photos.isEmpty()) {
                    Text(
                        strings.noPhotosYet,
                        color = theme.mutedText,
                        style = MaterialTheme.typography.bodyMedium
                    )
                } else {
                    LazyRow(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                        items(photos, key = { it.id }) { photo ->
                            val index = photos.indexOfFirst { it.id == photo.id }
                            PropertyPhotoTile(
                                photo = photo,
                                index = index,
                                mainLabel = strings.mainPhoto,
                                imageDescription = strings.propertyPhotoContentDescription,
                                deleteDescription = strings.deletePhoto,
                                moveLeftDescription = strings.movePhotoLeft,
                                moveRightDescription = strings.movePhotoRight,
                                canMoveLeft = index > 0,
                                canMoveRight = index < photos.lastIndex,
                                onDelete = { onDeletePhoto(photo.id) },
                                onMoveLeft = { onMovePhoto(photo.id, -1) },
                                onMoveRight = { onMovePhoto(photo.id, 1) },
                                onMakeMain = { onMakeMainPhoto(photo.id) }
                            )
                        }
                    }
                }
            }
        }
    }

    HavnButton(
        text = if (mode == PropertiesMode.Add) strings.createPropertyButton else strings.commonSave,
        onClick = onSave,
        modifier = actionButtonModifier,
        loading = isSaving
    )
}

@Composable
private fun PropertyLocationScreen(
    form: PropertyFormState,
    onCoordinateChanged: (HavnMapCoordinate) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val address = form.fullAddress()
    val coordinate = form.mapCoordinate()
    var searchText by remember(address) { mutableStateOf(address) }
    var lookupText by remember(address) { mutableStateOf(address) }
    var lookupRequestId by remember { mutableStateOf(0) }
    val actionButtonModifier = if (havnIsWebShell) {
        Modifier.widthIn(max = 360.dp)
    } else {
        Modifier.fillMaxWidth()
    }
    val mapShape = RoundedCornerShape(8.dp)

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            HavnTextField(
                value = searchText,
                onValueChange = { searchText = it },
                label = strings.findLocation
            )
            HavnButton(
                text = strings.find,
                onClick = {
                    lookupText = searchText
                    lookupRequestId += 1
                },
                style = HavnButtonStyle.Secondary,
                modifier = actionButtonModifier,
                enabled = searchText.isNotBlank()
            )
            coordinate?.let {
                Text(it.locationLabel(), color = theme.mutedText, style = MaterialTheme.typography.bodySmall)
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(420.dp)
            .clip(mapShape)
            .border(BorderStroke(1.dp, theme.border), mapShape)
    ) {
        HavnPropertyMap(
            address = lookupText,
            searchRequestId = lookupRequestId,
            markerTitle = strings.propertyLocationMarkerTitle,
            missingTokenMessage = strings.mapKitJsMissingToken,
            loadingMessage = strings.mapKitJsLoading,
            coordinate = coordinate,
            onCoordinateChanged = onCoordinateChanged,
            modifier = Modifier.fillMaxSize()
        )
    }

}

@Composable
private fun PropertyOwnerContactCard(
    contacts: List<Client>,
    selectedContactId: String?,
    strings: com.havn.app.core.localization.HavnStrings,
    onSelect: (String?) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val selectedContact = contacts.firstOrNull { it.id == selectedContactId }
    var showPicker by remember { mutableStateOf(false) }
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(
                strings.propertyOwnerContactTitle,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleLarge
            )
            if (contacts.isEmpty()) {
                Text(
                    strings.propertyOwnerNoContacts,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
            } else {
                selectedContact?.let { contact ->
                    Text(
                        strings.propertyOwnerSelected,
                        color = theme.mutedText,
                        style = MaterialTheme.typography.labelLarge
                    )
                    OwnerContactRow(
                        contact = contact,
                        selected = true,
                        onClick = { showPicker = true }
                    )
                } ?: Text(
                    strings.propertyOwnerSelectContact,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
                ) {
                    HavnButton(
                        text = if (selectedContact == null) strings.propertyOwnerChooseContact else strings.propertyOwnerChangeContact,
                        onClick = { showPicker = true },
                        style = HavnButtonStyle.Secondary,
                        modifier = Modifier.weight(1f),
                        leadingIcon = HavnIconKey.Search
                    )
                    if (selectedContact != null) {
                        HavnButton(
                            text = strings.propertyOwnerRemoveContact,
                            onClick = { onSelect(null) },
                            style = HavnButtonStyle.Ghost,
                            modifier = Modifier.weight(1f),
                            leadingIcon = HavnIconKey.Trash
                        )
                    }
                }
            }
        }
    }
    if (showPicker) {
        OwnerContactPickerDialog(
            contacts = contacts,
            selectedContactId = selectedContactId,
            strings = strings,
            onDismiss = { showPicker = false },
            onSelect = { contactId ->
                onSelect(contactId)
                showPicker = false
            }
        )
    }
}

@Composable
private fun OwnerContactPickerDialog(
    contacts: List<Client>,
    selectedContactId: String?,
    strings: com.havn.app.core.localization.HavnStrings,
    onDismiss: () -> Unit,
    onSelect: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    var query by remember { mutableStateOf("") }
    val normalizedQuery = query.trim()
    val matches = remember(contacts, normalizedQuery) {
        if (normalizedQuery.isBlank()) {
            emptyList()
        } else {
            contacts
                .asSequence()
                .filter { contact -> contact.matchesOwnerQuery(normalizedQuery) }
                .take(25)
                .toList()
        }
    }
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = theme.card,
        titleContentColor = theme.primaryText,
        textContentColor = theme.secondaryText,
        title = { Text(strings.propertyOwnerPickerTitle) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                HavnTextField(
                    value = query,
                    onValueChange = { query = it },
                    label = strings.propertyOwnerSearchContact,
                    leadingIcon = HavnIconKey.Search
                )
                Text(
                    if (normalizedQuery.isBlank()) strings.propertyOwnerSearchHint else "${matches.size} ${strings.clientsTitle.lowercase()}",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                if (normalizedQuery.isNotBlank() && matches.isEmpty()) {
                    Text(
                        strings.propertyOwnerNoMatches,
                        color = theme.mutedText,
                        style = MaterialTheme.typography.bodyMedium
                    )
                } else {
                    LazyColumn(
                        modifier = Modifier.heightIn(max = 360.dp),
                        verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
                    ) {
                        items(matches, key = { it.id }) { contact ->
                            OwnerContactRow(
                                contact = contact,
                                selected = contact.id == selectedContactId,
                                onClick = { onSelect(contact.id) }
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.commonCancel, color = theme.primaryAccent)
            }
        }
    )
}

@Composable
private fun OwnerContactRow(
    contact: Client,
    selected: Boolean,
    onClick: () -> Unit,
    trailingIcon: HavnIconKey = if (selected) HavnIconKey.Check else HavnIconKey.ArrowRight
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.medium)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.13f) else theme.surface,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Row(
            modifier = Modifier.padding(HavnSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
        ) {
            Surface(
                modifier = Modifier.size(42.dp),
                shape = MaterialTheme.shapes.large,
                color = theme.primaryAccent.copy(alpha = if (selected) 0.28f else 0.14f),
                border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.5f))
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(
                        contact.initials,
                        color = theme.primaryText,
                        style = MaterialTheme.typography.labelLarge
                    )
                }
            }
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    contact.fullName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.bodyLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    contact.contactLine,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.labelMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            HavnIcon(
                icon = trailingIcon,
                contentDescription = null,
                tint = if (selected) theme.primaryAccent else theme.mutedText,
                modifier = Modifier.size(18.dp)
            )
        }
    }
}

private val Client.initials: String
    get() = listOf(firstName, lastName)
        .mapNotNull { value -> value?.trim()?.firstOrNull()?.uppercase() }
        .take(2)
        .joinToString("")
        .ifBlank { fullName.trim().take(2).uppercase().ifBlank { "C" } }

private fun Client.matchesOwnerQuery(query: String): Boolean {
    val lowerQuery = query.lowercase()
    return fullName.lowercase().contains(lowerQuery) ||
        email.orEmpty().lowercase().contains(lowerQuery) ||
        phone.orEmpty().lowercase().contains(lowerQuery)
}

@Composable
private fun StatusPicker(
    selected: PropertyStatus,
    strings: com.havn.app.core.localization.HavnStrings,
    onSelected: (PropertyStatus) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.medium,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(3.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            PropertyStatus.entries.forEachIndexed { index, status ->
                val isSelected = selected == status
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .heightIn(min = 44.dp)
                        .clip(MaterialTheme.shapes.small)
                        .background(if (isSelected) theme.primaryAccent else theme.surface)
                        .clickable { onSelected(status) }
                        .padding(horizontal = HavnSpacing.sm, vertical = HavnSpacing.sm),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = status.localizedLabel(strings),
                        color = if (isSelected) theme.background else theme.primaryText,
                        style = MaterialTheme.typography.labelLarge,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                if (index != PropertyStatus.entries.lastIndex) {
                    Box(
                        modifier = Modifier
                            .width(1.dp)
                            .height(24.dp)
                            .background(theme.border)
                    )
                }
            }
        }
    }
}

@Composable
private fun ErrorPanel(error: String, onDismiss: () -> Unit) {
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Text(error, color = HavnDanger, style = MaterialTheme.typography.bodyLarge)
            HavnButton(
                text = strings.commonDismiss,
                onClick = onDismiss,
                style = HavnButtonStyle.Ghost,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun BoxScope.PropertyImageHeader(
    property: Property,
    selectedPhoto: PropertyPhoto? = null
) {
    val theme = LocalHavnThemeTokens.current
    val heroPhoto = selectedPhoto
        ?: property.photos.firstOrNull { it.storagePath == property.heroPhotoPath }
        ?: property.photos.firstOrNull { it.isHero }
        ?: property.photos.firstOrNull()
    Crossfade(
        targetState = heroPhoto,
        label = "property-photo-transition",
        modifier = Modifier.fillMaxSize()
    ) { photo ->
        Box(
            modifier = Modifier.fillMaxSize()
        ) {
            val rendered = HavnDatabaseImage(
                dataUrl = photo?.thumbnailDataUrl,
                contentDescription = property.title,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
            if (!rendered) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(
                            Brush.linearGradient(
                                colors = listOf(theme.surface, theme.primaryAccent.copy(alpha = 0.64f), theme.cold.copy(alpha = 0.42f))
                            )
                        )
                )
            }
        }
    }
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(androidx.compose.ui.graphics.Color.Transparent, androidx.compose.ui.graphics.Color(0xCC05080D))
                )
            )
    )
}

@Composable
private fun PropertyPhotoTile(
    photo: PropertyPhoto,
    index: Int,
    mainLabel: String,
    imageDescription: String,
    deleteDescription: String,
    moveLeftDescription: String,
    moveRightDescription: String,
    canMoveLeft: Boolean,
    canMoveRight: Boolean,
    onDelete: () -> Unit,
    onMoveLeft: () -> Unit,
    onMoveRight: () -> Unit,
    onMakeMain: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Column(
        modifier = Modifier.width(132.dp),
        verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs)
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(96.dp)
                .clip(MaterialTheme.shapes.medium)
                .border(1.dp, theme.primaryAccent.copy(alpha = if (index == 0) 0.9f else 0.28f), MaterialTheme.shapes.medium)
                .clickable(onClick = onMakeMain)
        ) {
            val rendered = HavnDatabaseImage(
                dataUrl = photo.thumbnailDataUrl,
                contentDescription = photo.altText ?: imageDescription,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
            if (!rendered) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(Brush.linearGradient(listOf(theme.surface, theme.primaryAccent.copy(alpha = 0.36f))))
                )
                HavnIcon(
                    icon = HavnIconKey.Properties,
                    contentDescription = null,
                    tint = theme.primaryAccent,
                    modifier = Modifier
                        .align(Alignment.Center)
                        .size(28.dp)
                )
            }
            if (index == 0) {
                Text(
                    mainLabel,
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .background(theme.primaryAccent.copy(alpha = 0.92f), MaterialTheme.shapes.small)
                        .padding(horizontal = HavnSpacing.sm, vertical = HavnSpacing.xs),
                    color = theme.background,
                    style = MaterialTheme.typography.labelSmall
                )
            }
            Box(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(HavnSpacing.xs)
                    .size(32.dp)
                    .clip(MaterialTheme.shapes.small)
                    .background(Color(0xCC05080D))
                    .clickable(onClick = onDelete),
                contentAlignment = Alignment.Center
            ) {
                HavnIcon(
                    icon = HavnIconKey.Trash,
                    contentDescription = deleteDescription,
                    tint = HavnDanger,
                    modifier = Modifier.size(18.dp)
                )
            }
        }

        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.xs), modifier = Modifier.fillMaxWidth()) {
            IconBoxButton(
                icon = HavnIconKey.Back,
                contentDescription = moveLeftDescription,
                enabled = canMoveLeft,
                onClick = onMoveLeft,
                modifier = Modifier.weight(1f)
            )
            IconBoxButton(
                icon = HavnIconKey.ArrowRight,
                contentDescription = moveRightDescription,
                enabled = canMoveRight,
                onClick = onMoveRight,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun IconBoxButton(
    icon: HavnIconKey,
    contentDescription: String,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = modifier
            .height(38.dp)
            .clip(MaterialTheme.shapes.small)
            .background(if (enabled) theme.surface else theme.surface.copy(alpha = 0.5f))
            .border(1.dp, theme.border.copy(alpha = if (enabled) 1f else 0.45f), MaterialTheme.shapes.small)
            .clickable(enabled = enabled, onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        HavnIcon(
            icon = icon,
            contentDescription = contentDescription,
            tint = if (enabled) theme.primaryAccent else theme.mutedText,
            modifier = Modifier.size(18.dp)
        )
    }
}

private fun PropertyStatus.toHavnStatus(): HavnStatus = when (this) {
    PropertyStatus.Active -> HavnStatus.Active
    PropertyStatus.Planned -> HavnStatus.Planned
    PropertyStatus.Archived -> HavnStatus.Archived
}

private fun PropertyStatus.localizedLabel(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    PropertyStatus.Active -> strings.commonActive
    PropertyStatus.Planned -> strings.commonPlanned
    PropertyStatus.Archived -> strings.commonArchived
}

private fun String.localizedPropertyValidation(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    "Property title is required." -> strings.propertyTitleRequired
    "Street address is required." -> strings.propertyStreetRequired
    "City is required." -> strings.propertyCityRequired
    "State or region is required." -> strings.propertyRegionRequired
    "Postal code is required." -> strings.propertyPostalRequired
    "Enter a valid asking price." -> strings.propertyAskingPriceInvalid
    else -> this
}

private fun String.numberText(): String = filter { it.isDigit() || it == '.' }.let { value ->
    val firstDot = value.indexOf('.')
    if (firstDot == -1) value else value.take(firstDot + 1) + value.drop(firstDot + 1).replace(".", "")
}

private fun PropertyFormState.fullAddress(): String =
    listOf(addressLine1, addressLine2, city, region, postalCode, country)
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .joinToString(", ")

private fun PropertyFormState.mapCoordinate(): HavnMapCoordinate? {
    val lat = latitude ?: return null
    val lng = longitude ?: return null
    return HavnMapCoordinate(lat, lng)
}

private fun Property.mapCoordinate(): HavnMapCoordinate? {
    val lat = latitude ?: return null
    val lng = longitude ?: return null
    return HavnMapCoordinate(lat, lng)
}

private fun PropertyFormState.locationLabel(): String? =
    mapCoordinate()?.locationLabel()

private fun HavnMapCoordinate.locationLabel(): String =
    "${latitude.formatCoordinate()}, ${longitude.formatCoordinate()}"

private fun Double.formatCoordinate(): String =
    (kotlin.math.round(this * 100000.0) / 100000.0).toString()

private fun String.displayDateTime(): String =
    replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
        .take(16)

private fun PropertiesMode.navigationDepth(): Int = when (this) {
    PropertiesMode.List -> 0
    PropertiesMode.Detail,
    PropertiesMode.Add -> 1
    PropertiesMode.Edit -> 2
    PropertiesMode.Locate -> 3
}
